import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

interface BulkDropItem {
  type: 'database' | 'schema' | 'table' | 'view' | 'function' | 'procedure' | 'trigger' | 'sequence' | 'extension' | 'index' | 'event' | 'user'
  name: string
  database: string
  schema?: string
}

interface BulkDropOperationData {
  items: BulkDropItem[]
  connectionId: string
  cascade?: boolean
}

interface BulkDropConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (cascade: boolean) => void
  bulkDropData: BulkDropOperationData | null
  isLoading: boolean
  progress?: { completed: number; total: number }
}

export function BulkDropConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  bulkDropData,
  isLoading,
  progress
}: BulkDropConfirmDialogProps) {
  if (!bulkDropData) return null

  const { items } = bulkDropData
  const itemCount = items.length
  
  // Group items by type for better display
  const itemsByType: Record<string, BulkDropItem[]> = {}
  for (const item of items) {
    if (!(item.type in itemsByType)) {
      itemsByType[item.type] = []
    }
    itemsByType[item.type].push(item)
  }

  // Check if there are dangerous operations (databases or schemas)
  const hasDangerousOperations = items.some((item: BulkDropItem) => 
    item.type === 'database' || item.type === 'schema'
  )

  const handleConfirm = (cascade = false) => {
    onConfirm(cascade)
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    hasDangerousOperations 
                      ? 'bg-red-100 dark:bg-red-900/30' 
                      : 'bg-yellow-100 dark:bg-yellow-900/30'
                  }`}>
                    <ExclamationTriangleIcon className={`w-6 h-6 ${
                      hasDangerousOperations 
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-yellow-600 dark:text-yellow-400'
                    }`} />
                  </div>
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100"
                    >
                      {hasDangerousOperations ? 'Dangerous Operation' : 'Confirm Bulk Drop'}
                    </Dialog.Title>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      You are about to drop {itemCount} item{itemCount > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {hasDangerousOperations && (
                  <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                      ⚠️ This operation includes dropping databases or schemas, which is irreversible and will destroy all contained data.
                    </p>
                  </div>
                )}

                <div className="mb-6 space-y-3">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100">
                    Items to be dropped:
                  </h4>
                  {isLoading && progress ? (
                    <div className="space-y-3">
                      <div className="relative pt-1">
                        <div className="flex mb-2 items-center justify-between">
                          <div>
                            <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200 dark:text-blue-200 dark:bg-blue-900">
                              Progress
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-xs font-semibold inline-block text-blue-600 dark:text-blue-400">
                              {progress.completed}/{progress.total}
                            </span>
                          </div>
                        </div>
                        <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-200 dark:bg-blue-900">
                          <div 
                            style={{ width: `${(progress.completed / progress.total) * 100}%` }}
                            className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-300 ease-out"
                          />
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                        Dropping items... Please wait.
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                      {Object.entries(itemsByType).map(([type, typeItems]) => (
                        <div key={type} className="mb-2 last:mb-0">
                          <div className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">
                            {type}s ({typeItems.length})
                          </div>
                          <div className="space-y-1">
                            {typeItems.map((item: BulkDropItem, index: number) => (
                              <div key={index} className="text-sm text-gray-700 dark:text-gray-300 pl-2">
                                <span className="text-blue-600 dark:text-blue-400 font-mono">
                                  {item.database}
                                </span>
                                {item.schema && (
                                  <>
                                    <span className="text-gray-500 mx-1">.</span>
                                    <span className="text-green-600 dark:text-green-400 font-mono">
                                      {item.schema}
                                    </span>
                                  </>
                                )}
                                <span className="text-gray-500 mx-1">.</span>
                                <span className="font-mono">{item.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {!isLoading && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="cascade"
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label 
                        htmlFor="cascade" 
                        className="text-sm text-gray-700 dark:text-gray-300"
                      >
                        Cascade delete (drop dependent objects)
                      </label>
                    </div>
                  </div>
                )}

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    onClick={onClose}
                    disabled={false}
                  >
                    {isLoading ? 'Cancel Operation' : 'Cancel'}
                  </button>
                  <button
                    type="button"
                    className={`inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${
                      hasDangerousOperations
                        ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
                        : 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                    }`}
                    onClick={() => {
                      const element = document.getElementById('cascade')
                      const checked = element instanceof HTMLInputElement ? element.checked : false
                      handleConfirm(checked)
                    }}
                    disabled={isLoading}
                  >
                    {isLoading && progress ? `Dropping... (${progress.completed}/${progress.total})` : `Drop ${itemCount} Item${itemCount > 1 ? 's' : ''}`}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
